import { c as _c } from "react/compiler-runtime";
import { Box, Separator, VStack } from '@chakra-ui/react';
import { WIL_DISPLAY_NAME } from '@src/components/sco/Advisors/constants';
import { useAdvisorsAttention } from '@src/components/sco/Advisors/hooks/useAdvisorsAttention';
import { BecomeAdvisorNavCard } from '@src/components/sco/Advisors/layout/BecomeAdvisorNavCard.tsx';
import { NavGroup } from '@src/components/sco/Advisors/layout/NavGroup';
import { AdvisorsPaths, useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { isAdvisorProfileHubEligible, isAdvisorProfileHubPath, isPublicAdvisorProfilePath } from '@src/components/sco/Advisors/utils/advisorProfileHub';
import { AdvisorsNavAttention, advisorsNavAttention, attentionBadgeProps, EMPTY_ADVISORS_ATTENTION } from '@src/components/sco/Advisors/utils/advisorsAttention';
import { SidebarNestedNavList, SidebarNestedNavListSection } from '@src/components/sco/SidebarNestedNavList';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { LuCalculator, LuFileText, LuIdCard, LuMessageSquare, LuPhoneCall, LuRocket, LuSearch, LuSparkles, LuTarget, LuUsers } from 'react-icons/lu';
const normalizePath = (path: string) => path.split('?')[0].split('#')[0];
const isPathActive = (asPath: string, href: string, options?: {
  exact?: boolean;
  alsoActive?: string[];
}) => {
  const path = normalizePath(asPath);
  const paths = [href, ...(options?.alsoActive ?? [])];
  return paths.some(p => options?.exact ? path === p : path === p || p.length > 1 && path.startsWith(`${p}/`));
};
const NO_NAV_ATTENTION = advisorsNavAttention(EMPTY_ADVISORS_ATTENTION);

/**
 * Capability-gated Advisors left nav (no Founder/Advisor mode toggle).
 *
 * - Ask — Wil-AI, Messages (Inbox), Sessions
 * - Manage / Recruit / Tools — demand items always; supply items added when advisor-capable
 * - Become card — non-advisors only
 * - Hrefs follow `useAdvisorsPaths()` so Request Workspace embeds stay under manage/...
 * - Your-turn badges (W3) come from the shared `useAdvisorsAttention` payload, so the standalone
 *   site, the Request Workspace embed and Startup OS all show the same numbers.
 *
 * `activePath` overrides `router.asPath` for hosts that route in-window (Startup OS).
 */
export const AdvisorsNav = t0 => {
  const $ = _c(10);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    activePath
  } = t1;
  const router = useRouter();
  const {
    currentUser
  } = useCurrentUser();
  const paths = useAdvisorsPaths();
  const {
    nav
  } = useAdvisorsAttention();
  const t2 = activePath ?? router.asPath;
  const t3 = currentUser?.advisorProfile?.statusKey;
  let t4;
  if ($[2] !== t3) {
    t4 = isAdvisorProfileHubEligible(t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = !!currentUser?.advisorProfile?.isApproved;
  let t6;
  if ($[4] !== nav || $[5] !== paths || $[6] !== t2 || $[7] !== t4 || $[8] !== t5) {
    t6 = <AdvisorsNavView asPath={t2} attention={nav} hasAdvisorProfileHub={t4} isMarketplaceAdvisor={t5} paths={paths} />;
    $[4] = nav;
    $[5] = paths;
    $[6] = t2;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
};
export type AdvisorsNavViewProps = {
  asPath: string;
  paths: AdvisorsPaths;
  /** Approved marketplace advisor: adds My Founders and Opportunities. */
  isMarketplaceAdvisor: boolean;
  /** Submitted an advisor profile (any hub status): adds My Advisor Profile, hides the Become card. */
  hasAdvisorProfileHub: boolean;
  /** Your-turn count per parent; 0 hides that badge. */
  attention?: AdvisorsNavAttention;
};

/** Presentational Advisors left nav. `AdvisorsNav` wires router, viewer and badge counts. */
export const AdvisorsNavView = t0 => {
  const $ = _c(107);
  const {
    asPath,
    paths,
    isMarketplaceAdvisor,
    hasAdvisorProfileHub,
    attention: t1
  } = t0;
  const attention = t1 === undefined ? NO_NAV_ATTENTION : t1;
  const canUseAdvisorItems = isMarketplaceAdvisor;
  const showBecomeAdvisorCard = !hasAdvisorProfileHub;
  const t2 = paths.ask;
  let t3;
  if ($[0] !== asPath || $[1] !== paths.ask) {
    t3 = isPathActive(asPath, paths.ask);
    $[0] = asPath;
    $[1] = paths.ask;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== paths.ask || $[4] !== t3) {
    t4 = {
      title: WIL_DISPLAY_NAME,
      href: t2,
      icon: LuSparkles,
      isActive: t3
    };
    $[3] = paths.ask;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = paths.inbox;
  let t6;
  if ($[6] !== asPath || $[7] !== paths.inbox) {
    t6 = isPathActive(asPath, paths.inbox);
    $[6] = asPath;
    $[7] = paths.inbox;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== attention.messages) {
    t7 = attentionBadgeProps(attention.messages);
    $[9] = attention.messages;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== paths.inbox || $[12] !== t6 || $[13] !== t7) {
    t8 = {
      title: "Messages",
      href: t5,
      icon: LuMessageSquare,
      isActive: t6,
      badgeProps: t7
    };
    $[11] = paths.inbox;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = paths.sessions;
  let t10;
  if ($[15] !== asPath || $[16] !== paths.sessions) {
    t10 = isPathActive(asPath, paths.sessions);
    $[15] = asPath;
    $[16] = paths.sessions;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== attention.sessions) {
    t11 = attentionBadgeProps(attention.sessions);
    $[18] = attention.sessions;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== paths.sessions || $[21] !== t10 || $[22] !== t11) {
    t12 = {
      title: "Sessions",
      href: t9,
      icon: LuPhoneCall,
      isActive: t10,
      badgeProps: t11
    };
    $[20] = paths.sessions;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12 || $[25] !== t4 || $[26] !== t8) {
    t13 = [t4, t8, t12];
    $[24] = t12;
    $[25] = t4;
    $[26] = t8;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const primarySections = t13;
  let t14;
  if ($[28] !== asPath || $[29] !== paths.agreement || $[30] !== paths.agreements || $[31] !== paths.sign) {
    t14 = isPathActive(asPath, paths.agreements, {
      alsoActive: [paths.sign, paths.agreement]
    });
    $[28] = asPath;
    $[29] = paths.agreement;
    $[30] = paths.agreements;
    $[31] = paths.sign;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const onAgreementDetail = t14;
  const t15 = paths.myAdvisors;
  let t16;
  if ($[33] !== asPath || $[34] !== paths.myAdvisors || $[35] !== paths.seats) {
    t16 = isPathActive(asPath, paths.myAdvisors, {
      alsoActive: [paths.seats]
    });
    $[33] = asPath;
    $[34] = paths.myAdvisors;
    $[35] = paths.seats;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== attention.myAdvisors) {
    t17 = attentionBadgeProps(attention.myAdvisors);
    $[37] = attention.myAdvisors;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== paths.myAdvisors || $[40] !== t16 || $[41] !== t17) {
    t18 = {
      title: "My Advisors",
      href: t15,
      icon: LuUsers,
      isActive: t16,
      badgeProps: t17
    };
    $[39] = paths.myAdvisors;
    $[40] = t16;
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== asPath || $[44] !== canUseAdvisorItems || $[45] !== paths.myFounders) {
    t19 = canUseAdvisorItems ? [{
      title: "My Founders",
      href: paths.myFounders,
      icon: LuRocket,
      isActive: isPathActive(asPath, paths.myFounders)
    }] : [];
    $[43] = asPath;
    $[44] = canUseAdvisorItems;
    $[45] = paths.myFounders;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  const t20 = paths.agreements;
  let t21;
  if ($[47] !== attention.agreements) {
    t21 = attentionBadgeProps(attention.agreements);
    $[47] = attention.agreements;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== onAgreementDetail || $[50] !== paths.agreements || $[51] !== t21) {
    t22 = {
      title: "FAST Agreements",
      href: t20,
      icon: LuFileText,
      isActive: onAgreementDetail,
      badgeProps: t21
    };
    $[49] = onAgreementDetail;
    $[50] = paths.agreements;
    $[51] = t21;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== t18 || $[54] !== t19 || $[55] !== t22) {
    t23 = [t18, ...t19, t22];
    $[53] = t18;
    $[54] = t19;
    $[55] = t22;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  const manageSections = t23;
  const t24 = paths.ownProfile;
  let t25;
  if ($[57] !== asPath || $[58] !== paths.ownProfile) {
    t25 = isAdvisorProfileHubPath(asPath, paths.ownProfile);
    $[57] = asPath;
    $[58] = paths.ownProfile;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== paths.ownProfile || $[61] !== t25) {
    t26 = {
      title: "My Advisor Profile",
      href: t24,
      icon: LuIdCard,
      isActive: t25
    };
    $[60] = paths.ownProfile;
    $[61] = t25;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  const ownProfileNavItem = t26;
  let t27;
  if ($[63] !== asPath || $[64] !== paths.browse || $[65] !== paths.directory || $[66] !== paths.profile) {
    t27 = isPathActive(asPath, paths.directory) || isPathActive(asPath, paths.browse) || isPublicAdvisorProfilePath(asPath, paths.profile);
    $[63] = asPath;
    $[64] = paths.browse;
    $[65] = paths.directory;
    $[66] = paths.profile;
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  let t28;
  if ($[68] !== paths.directory || $[69] !== t27) {
    t28 = {
      title: "Find Advisors",
      href: paths.directory,
      icon: LuSearch,
      isActive: t27
    };
    $[68] = paths.directory;
    $[69] = t27;
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  let t29;
  if ($[71] !== asPath || $[72] !== attention.opportunities || $[73] !== canUseAdvisorItems || $[74] !== paths.opportunities) {
    t29 = canUseAdvisorItems ? [{
      title: "Opportunities",
      href: paths.opportunities,
      icon: LuTarget,
      isActive: isPathActive(asPath, paths.opportunities),
      badgeProps: attentionBadgeProps(attention.opportunities)
    }] : [];
    $[71] = asPath;
    $[72] = attention.opportunities;
    $[73] = canUseAdvisorItems;
    $[74] = paths.opportunities;
    $[75] = t29;
  } else {
    t29 = $[75];
  }
  let t30;
  if ($[76] !== hasAdvisorProfileHub || $[77] !== ownProfileNavItem) {
    t30 = hasAdvisorProfileHub ? [ownProfileNavItem] : [];
    $[76] = hasAdvisorProfileHub;
    $[77] = ownProfileNavItem;
    $[78] = t30;
  } else {
    t30 = $[78];
  }
  let t31;
  if ($[79] !== t28 || $[80] !== t29 || $[81] !== t30) {
    t31 = [t28, ...t29, ...t30];
    $[79] = t28;
    $[80] = t29;
    $[81] = t30;
    $[82] = t31;
  } else {
    t31 = $[82];
  }
  const recruitSections = t31;
  const t32 = paths.compCalculator;
  let t33;
  if ($[83] !== asPath || $[84] !== paths.compCalculator) {
    t33 = isPathActive(asPath, paths.compCalculator, {
      exact: true
    });
    $[83] = asPath;
    $[84] = paths.compCalculator;
    $[85] = t33;
  } else {
    t33 = $[85];
  }
  let t34;
  if ($[86] !== paths.compCalculator || $[87] !== t33) {
    t34 = [{
      title: "Comp Calculator",
      href: t32,
      icon: LuCalculator,
      isActive: t33
    }];
    $[86] = paths.compCalculator;
    $[87] = t33;
    $[88] = t34;
  } else {
    t34 = $[88];
  }
  const toolsSections = t34;
  let t35;
  if ($[89] !== primarySections) {
    t35 = <NavGroup label="Ask"><SidebarNestedNavList accordionBehavior="expand-only" sections={primarySections} /></NavGroup>;
    $[89] = primarySections;
    $[90] = t35;
  } else {
    t35 = $[90];
  }
  let t36;
  if ($[91] !== manageSections) {
    t36 = <NavGroup label="Manage"><SidebarNestedNavList accordionBehavior="expand-only" sections={manageSections} /></NavGroup>;
    $[91] = manageSections;
    $[92] = t36;
  } else {
    t36 = $[92];
  }
  let t37;
  if ($[93] !== recruitSections) {
    t37 = <NavGroup label="Recruit"><SidebarNestedNavList accordionBehavior="expand-only" sections={recruitSections} /></NavGroup>;
    $[93] = recruitSections;
    $[94] = t37;
  } else {
    t37 = $[94];
  }
  let t38;
  if ($[95] !== toolsSections) {
    t38 = <NavGroup label="Tools"><SidebarNestedNavList accordionBehavior="expand-only" sections={toolsSections} /></NavGroup>;
    $[95] = toolsSections;
    $[96] = t38;
  } else {
    t38 = $[96];
  }
  let t39;
  if ($[97] !== t35 || $[98] !== t36 || $[99] !== t37 || $[100] !== t38) {
    t39 = <Box flex={1} minHeight={0} overflowY="auto" paddingX={1} paddingY={4} width="full"><VStack align="stretch" gap={5} width="full">{t35}{t36}{t37}{t38}</VStack></Box>;
    $[97] = t35;
    $[98] = t36;
    $[99] = t37;
    $[100] = t38;
    $[101] = t39;
  } else {
    t39 = $[101];
  }
  let t40;
  if ($[102] !== showBecomeAdvisorCard) {
    t40 = showBecomeAdvisorCard && <><Separator /><Box marginTop={3} paddingX={1} width="full"><BecomeAdvisorNavCard /></Box></>;
    $[102] = showBecomeAdvisorCard;
    $[103] = t40;
  } else {
    t40 = $[103];
  }
  let t41;
  if ($[104] !== t39 || $[105] !== t40) {
    t41 = <VStack align="stretch" gap={0} height="full" minHeight={0} width="full">{t39}{t40}</VStack>;
    $[104] = t39;
    $[105] = t40;
    $[106] = t41;
  } else {
    t41 = $[106];
  }
  return t41;
};